import type { Invoice, HeaderEdits } from '../types';

/**
 * Initial header edit state = ONLY what is stored on the invoice.
 * The credit card is never pre-filled from the global or vendor default here:
 * showing a card that is not persisted would let a reviewer approve an invoice
 * that then fails the WinTeam "no Credit Card is selected" gate.
 */
export function buildInitialHeaderEdits(inv: Invoice): HeaderEdits {
  return {
    invoice_amount: inv.invoice_amount,
    invoice_date: inv.invoice_date,
    invoice_number: inv.invoice_number,
    payment_method_id: inv.payment_method_id ?? 1,
    credit_card_vendor_number: inv.payment_method_id === 2 ? (inv.credit_card_vendor_number ?? null) : null,
    posting_date: inv.posting_date || inv.invoice_date,
    vendor_number: inv.vendor_number,
    vendor_name: inv.vendor_name,
  };
}

/**
 * Payment-method change on the edit draft. Switching to Credit Card keeps only a card
 * that is already on the draft (stored or explicitly picked) — it never seeds the
 * GLOBAL default card. Any non-CC method clears the card.
 */
export function applyPaymentMethodToHeaderEdits(edits: HeaderEdits, newMethod: number): HeaderEdits {
  return {
    ...edits,
    payment_method_id: newMethod,
    credit_card_vendor_number: newMethod === 2 ? (edits.credit_card_vendor_number ?? null) : null,
  };
}

/**
 * Normalise one header field for dirty comparison:
 *  - invoice_amount: compared numerically (inputs may hold "100" vs 100)
 *  - everything else (including posting_date): null/undefined collapse to null.
 *    A cleared posting date ('') is a real change and stays dirty, so Approve is
 *    gated behind Save and the backend (which rejects an empty date) decides.
 */
function normalizeHeaderField(k: keyof HeaderEdits, v: unknown): unknown {
  if (k === 'invoice_amount') return Number(v);
  return v ?? null;
}

/** True when the header edits differ from the invoice's stored (saved) values. */
export function headerEditsDirty(inv: Invoice | null, edits: HeaderEdits | null): boolean {
  if (!inv || !edits) return false;
  const base = buildInitialHeaderEdits(inv);
  return (Object.keys(base) as (keyof HeaderEdits)[]).some(
    k => normalizeHeaderField(k, base[k]) !== normalizeHeaderField(k, edits[k])
  );
}

/** Saved-baseline refresh: apply the header edits onto the invoice after a successful save. */
export function applyHeaderEditsToInvoice(inv: Invoice, edits: HeaderEdits): Invoice {
  return {
    ...inv,
    invoice_amount: edits.invoice_amount,
    invoice_date: edits.invoice_date,
    invoice_number: edits.invoice_number,
    payment_method_id: edits.payment_method_id,
    credit_card_vendor_number: edits.payment_method_id === 2 ? edits.credit_card_vendor_number : null,
    // Only called after a successful save, so this is the date that was actually sent
    // and stored. No fallback is invented here: the backend rejects an empty date, so
    // a save with '' never succeeds and never reaches this function.
    posting_date: edits.posting_date,
    vendor_number: edits.vendor_number,
    vendor_name: edits.vendor_name,
  };
}
